import React from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Menu,
  Paper,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCalendar,
  IconCheck,
  IconDots,
  IconTrash,
  IconUser,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PerformanceReviewRound } from '../../../../api/generated/model/performanceReviewRound';
import { PerformanceReviewRoundStage } from '../../../../api/generated/model/performanceReviewRoundStage';
import { Eyebrow } from '../../parts/primitives';

const STAGES: PerformanceReviewRoundStage[] = [
  'draft',
  'collect-essay',
  'collect-feedback',
  'summary',
  'finished',
];

const StepDot: React.FC<{ status: 'done' | 'active' | 'todo'; index: number }> = ({
  status,
  index,
}) => {
  if (status === 'done') {
    return (
      <ThemeIcon radius="xl" size={24} color="teal" variant="filled">
        <IconCheck size={13} />
      </ThemeIcon>
    );
  }
  if (status === 'active') {
    return (
      <ThemeIcon
        radius="xl"
        size={24}
        color="teal"
        variant="outline"
        style={{
          boxShadow: '0 0 0 4px var(--mantine-primary-color-light)',
          fontWeight: 600,
          fontSize: 11,
        }}
      >
        {index}
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon
      radius="xl"
      size={24}
      color="gray"
      variant="default"
      style={{ fontSize: 11, fontWeight: 500 }}
    >
      {index}
    </ThemeIcon>
  );
};

interface StageStepperProps {
  stages: { key: PerformanceReviewRoundStage; label: string; deadline?: string | Date }[];
  current: PerformanceReviewRoundStage;
  locale?: string;
}

const StageStepper: React.FC<StageStepperProps> = ({ stages, current, locale = 'ru-RU' }) => {
  const { t } = useTranslation();
  const idx = Math.max(
    0,
    stages.findIndex((s) => s.key === current),
  );
  const now = new Date();
  const formatDeadline = (d?: string | Date) => {
    if (!d) return null;
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };
  return (
    <Flex gap={0} align="stretch" style={{ scrollbarWidth: 'thin' }}>
      {stages.map((s, i) => {
        const status: 'done' | 'active' | 'todo' = i < idx ? 'done' : i === idx ? 'active' : 'todo';
        const isLast = i === stages.length - 1;
        const deadlineLabel = formatDeadline(s.deadline);
        const deadlineDate = s.deadline ? new Date(s.deadline) : undefined;
        const isOverdue =
          deadlineDate &&
          status === 'active' &&
          !Number.isNaN(deadlineDate.getTime()) &&
          deadlineDate.getTime() < now.getTime();
        return (
          <Box
            key={s.key}
            style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column' }}
          >
            <Group gap={10} mb={8} wrap="nowrap" align="center">
              <StepDot status={status} index={i + 1} />
              {!isLast && (
                <Box
                  style={{
                    flex: 1,
                    height: 2,
                    background:
                      status === 'done'
                        ? 'var(--mantine-primary-color-filled)'
                        : status === 'active'
                          ? 'linear-gradient(90deg, var(--mantine-primary-color-filled), var(--mantine-color-default-border))'
                          : 'var(--mantine-color-default-border)',
                    borderRadius: 999,
                  }}
                />
              )}
            </Group>
            <Box pr={isLast ? 0 : 16}>
              <Eyebrow color={status === 'active' ? 'teal' : 'dimmed'}>
                {t('perfReview.home.hero.step', { n: String(i + 1).padStart(2, '0') })}
              </Eyebrow>
              <Text size="sm" fw={600} c={status === 'todo' ? 'dimmed' : undefined} mt={4}>
                {s.label}
              </Text>
              {deadlineLabel && (
                <Text
                  size="xs"
                  mt={2}
                  c={isOverdue ? 'red' : status === 'todo' ? 'dimmed' : undefined}
                >
                  {isOverdue
                    ? t('perfReview.stageDeadlines.overdue', {
                        date: deadlineLabel,
                      })
                    : t('perfReview.stageDeadlines.until', {
                        date: deadlineLabel,
                      })}
                </Text>
              )}
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
};

interface RoundDetailHeroProps {
  round: PerformanceReviewRound;
  stageLabel: (s: string) => string;
  locale: string;
  nextStageKey?: PerformanceReviewRoundStage | 'archived';
  onMoveNext?: () => void;
  isMoving?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  isMobile?: boolean;
}

const formatDate = (d?: string, locale = 'ru-RU') => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
};

export const RoundDetailHero: React.FC<RoundDetailHeroProps> = ({
  round,
  stageLabel,
  locale,
  nextStageKey,
  onMoveNext,
  isMoving,
  onDelete,
  isDeleting,
  isMobile,
}) => {
  const { t } = useTranslation();
  const status = round.status || 'draft';
  const isArchived = status === 'archived';
  const stageDeadlineByKey: Record<PerformanceReviewRoundStage, string | undefined> = {
    draft: undefined,
    'collect-essay': round.stageDeadlines?.collectEssay,
    'collect-feedback': round.stageDeadlines?.collectFeedback,
    summary: round.stageDeadlines?.summary,
    finished: round.endDate,
  };
  const stages = STAGES.map((key) => ({
    key,
    label: stageLabel(key),
    deadline: stageDeadlineByKey[key],
  }));

  const statusBadge = (
    <Badge
      color={isArchived ? 'gray' : status === 'active' ? 'teal' : 'orange'}
      variant="light"
      size="sm"
      radius="xl"
      leftSection={
        <Box style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
      }
    >
      {t(`perfReview.adminHome.status.${status}`, { defaultValue: status })}
    </Badge>
  );

  return (
    <Paper withBorder radius="lg" p={0} style={{ overflow: 'hidden' }}>
      <Box
        p={isMobile ? 'md' : 'lg'}
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
      >
        <Flex align="flex-start" justify="space-between" gap="lg" wrap="wrap">
          <Box style={{ flex: 1, minWidth: isMobile ? 0 : 320 }}>
            <Group gap={6} mb="sm" wrap="wrap">
              {statusBadge}
              {(round.startDate || round.endDate) && (
                <Badge
                  variant="default"
                  size="sm"
                  radius="xl"
                  leftSection={<IconCalendar size={12} />}
                >
                  {formatDate(round.startDate, locale)} — {formatDate(round.endDate, locale)}
                </Badge>
              )}
              {round.questionnaire &&
                typeof round.questionnaire === 'object' &&
                (round.questionnaire as { name?: string }).name && (
                  <Badge variant="default" size="sm" radius="xl">
                    {(round.questionnaire as { name?: string }).name}
                  </Badge>
                )}
              {round.createdBy &&
                typeof round.createdBy === 'object' &&
                (round.createdBy as { name?: string }).name && (
                  <Badge
                    variant="default"
                    size="sm"
                    radius="xl"
                    leftSection={<IconUser size={12} />}
                  >
                    {(round.createdBy as { name?: string }).name}
                  </Badge>
                )}
            </Group>
            <Title
              order={1}
              size={isMobile ? 'h3' : 'h2'}
              mb={4}
              style={{ letterSpacing: '-0.01em' }}
            >
              {round.title}
            </Title>
            {round.description && (
              <Text c="dimmed" size="sm" lh={1.55} style={{ maxWidth: 720 }}>
                {round.description}
              </Text>
            )}
          </Box>

          <Group gap="xs" wrap="wrap">
            {nextStageKey && onMoveNext && (
              <Button
                variant="light"
                rightSection={<IconArrowRight size={14} />}
                onClick={onMoveNext}
                loading={isMoving}
              >
                {t('perfReview.moveTo', { name: stageLabel(nextStageKey) })}
              </Button>
            )}
            {onDelete && (
              <Menu shadow="md" position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    aria-label={t('perfReview.deleteRound.menuLabel')}
                    loading={isDeleting}
                  >
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={onDelete}>
                    {t('perfReview.deleteRound.button')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Flex>
      </Box>

      <Box p={isMobile ? 'md' : 'lg'} style={{ background: 'var(--mantine-color-default-hover)' }}>
        <StageStepper
          stages={stages}
          current={(round.stage as PerformanceReviewRoundStage) ?? 'draft'}
          locale={locale}
        />
      </Box>
    </Paper>
  );
};
